import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { fallbackProducts } from "@/constants/fallbackData";

const defaultCategories = [
  "All",
  ...new Set(fallbackProducts.map((product) => product.category)),
];

export default function Products() {
  const [products] = useState(fallbackProducts);
  const [categories] = useState(defaultCategories);
  const [active, setActive] = useState("All");
  const [loading] = useState(false);

  const visible = active === "All" ? products : products.filter((p) => p.category === active);

  return (
    <section id="products" data-testid="products-section" className="py-20 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <span className="overline">The Apothecary</span>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight">
              Remedies, quietly
              <span className="italic text-[#C05C46]"> crafted.</span>
            </h2>
          </div>
          <p className="md:col-span-5 text-lg leading-relaxed text-[#5C6B5D]">
            Each formulation follows a classical text, made in small batches,
            with herbs sourced from farms that still speak to the seasons.
          </p>
        </div>

        {/* Filters */}
        <div
          data-testid="product-filters"
          className="mt-12 flex flex-wrap items-center gap-2"
        >
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase().replace(/\s+&\s+|\s+/g, "-")}`}
              onClick={() => setActive(c)}
              className={`px-5 py-2 rounded-full text-sm transition-all duration-300 border ${
                active === c
                  ? "bg-[#2E3A2F] text-[#F8F5F0] border-[#2E3A2F]"
                  : "bg-transparent text-[#2E3A2F] border-[#DCD3C6] hover:border-[#2E3A2F]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {loading &&
            Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="animate-pulse bg-[#EBE4D8] rounded-3xl h-[420px]"
              />
            ))}
          {!loading &&
            visible.map((p) => (
              <article
                key={p.id}
                data-testid={`product-card-${p.id}`}
                className="group relative bg-[#EBE4D8] border border-[#DCD3C6] rounded-3xl overflow-hidden transition-transform duration-500 hover:-translate-y-1"
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute top-4 left-4 bg-[#F8F5F0]/90 backdrop-blur px-3 py-1 rounded-full text-xs tracking-[0.2em] uppercase text-[#2E3A2F]">
                    {p.category}
                  </span>
                </div>
                <div className="p-6 md:p-7">
                  <h3 className="font-serif-display text-2xl md:text-3xl leading-tight text-[#2E3A2F]">
                    {p.name}
                  </h3>
                  <p className="mt-2 italic text-[#5C6B5D]">{p.tagline}</p>
                  <p className="mt-4 text-sm leading-relaxed text-[#5C6B5D] line-clamp-3">
                    {p.description}
                  </p>
                  <div className="mt-6 flex items-center justify-between">
                    <span className="font-serif-display text-xl text-[#C05C46]">
                      {p.price}
                    </span>
                    <a
                      href="#contact"
                      className="inline-flex items-center gap-1 text-sm text-[#2E3A2F] hover:text-[#C05C46] transition-colors"
                    >
                      Discover
                      <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                    </a>
                  </div>
                </div>
              </article>
            ))}
        </div>

        {!loading && visible.length === 0 && (
          <p
            data-testid="products-empty"
            className="mt-12 text-center text-[#5C6B5D]"
          >
            No remedies found in this category yet.
          </p>
        )}
      </div>
    </section>
  );
}
